// material-ui
import {
  Avatar,
  Badge,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  TooltipProps,
  Typography,
  tooltipClasses
} from '@mui/material';

// project import
import MainCard from 'components/MainCard';

// types
import { IAITag, IDeviceData, IUser } from 'models/IUser';
import { useNavigate } from 'react-router';
import moment from 'moment';
import 'moment/locale/ca';
import 'moment/locale/sl';
import { Box, styled } from '@mui/system';
import React, { useState } from 'react';
import { getDeviceBrandName, getSensorTypeName } from 'helpers/ExternalDeviceHelper';
import { ISensorData } from 'models/ISensor';
import { useGetSensorTypesQuery } from 'features/api/apiSlice';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getAvatar } from 'helpers/UserHelper';
import { useTranslation } from 'react-i18next';
import { IExternalDevice } from 'models/IExternalDevice';


// ==============================|| CUSTOMER - CARD ||============================== //

const PatientCard = ({ patient, externalDevices }: { patient: IUser, externalDevices: IExternalDevice[] }) => {
  const { t, i18n } = useTranslation();
  const { data: sensorTypes = [] } = useGetSensorTypesQuery();

  const navigate = useNavigate();

  const handleClickPatientFile = () => {
    const patientDisplayName = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim();

    return navigate(`/patients/${patient.id}/file`, {
      state: {
        patientId: patient.id,
        patientDisplayName
      }
    });
  };

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: 12,
      border: '1px solid #dadde9',
    },
  }));

  const hasPatientValuesOlderThan24h = () => {
    if (patient.devicesData) {
      return patient.devicesData.some((deviceData: IDeviceData) => {
        return deviceData.sensorsData.some(x => (moment.duration(moment(new Date()).diff(x.lastValueAt.toString())).asHours() > 24 ))
      })
    }

    return false
  }

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  }

  const handleMouseLeave = () => {
    setIsHovered(false);
  }

  return (
    <div 
      onClick={handleClickPatientFile} 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: 'pointer', boxShadow: isHovered ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none' }} >
      <MainCard sx={{ height: 1, '& .MuiCardContent-root': { height: 1, display: 'flex', flexDirection: 'column' } }}>
        <Grid id="print" container spacing={1.5}>
          <Grid item xs={12}>
            <List sx={{ width: 1, p: 0 }}>
              <ListItem disablePadding>
                <ListItemAvatar
                  style={{ marginRight: "15px" }}>
                  <Avatar src={getAvatar(patient.id!!)} sx={{ width: 60, height: 60, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }} />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box marginBottom={1}>
                      <Typography fontSize={14} fontWeight="bold" lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{patient.lastName}</Typography>
                      <Typography color="grey" fontSize={14} lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{patient.firstName}</Typography>
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid container item wrap="nowrap" spacing={1}>
            <Grid item xs={true} minWidth="20%" lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              <b>{t('enrolment', 'Enrolment')}</b>
            </Grid>
            <Grid item lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {patient.enrolmentAt ? moment(patient.enrolmentAt.toString()).locale(i18n.language).format('l') : "—"}
            </Grid>
          </Grid>
          <Grid container item wrap="nowrap" spacing={1}>
            <Grid item xs={true} minWidth="20%" lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              <b>{t('lastSyncedAt', 'Last synced')}</b>
            </Grid>
            <Grid item lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {patient.lastSyncedAt ? moment(patient.lastSyncedAt.toString()).locale(i18n.language).fromNow() : "—"}
              {patient.lastSyncedFrom && (
                <Badge
                  badgeContent={getDeviceBrandName(patient.lastSyncedFrom)}
                  color="default"
                  sx={{
                    ml: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    verticalAlign: 'middle',
                    '& .MuiBadge-badge': {
                      position: 'static',
                      transform: 'translateY(-1px)',
                      border: '1.5px solid',
                      borderColor: 'text.secondary',
                      bgcolor: 'transparent',
                      px: 0.75,
                      py: 0,
                      fontSize: '11px',
                      fontWeight: 700,
                      lineHeight: 'normal'
                    }
                  }}
                >
                  <span />
                </Badge>
              )}
            </Grid>
          </Grid>
          <Grid container item xs={12} wrap="nowrap" spacing={1}>
            <Grid item xs={true} minWidth="20%" lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              <b>{t('last_measurement', 'Last measurement')}</b>
            </Grid>
            <Grid item lineHeight="normal" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              { patient.lastValueAt ? moment(patient.lastValueAt.toString()).locale(i18n.language).fromNow() : "—" }
            </Grid>
            {
              patient.devicesData &&
              <Grid item lineHeight="normal" xs="auto">
                <HtmlTooltip title={
                  <React.Fragment>
                    {
                      patient.devicesData?.map((deviceData: IDeviceData, index: number) => (
                        <Grid container key={index}>
                          <Grid item xs={12}><u>{externalDevices.find(externalDevice => externalDevice.id === deviceData.id)?.name || `Unknown`}</u></Grid>
                          <Grid container item xs={12}>
                            {
                              deviceData.sensorsData && deviceData.sensorsData.length > 0 ? (
                                deviceData.sensorsData.map((sensorData: ISensorData, sensorIndex: number) => (
                                  <Grid item key={sensorIndex} style={(moment.duration(moment(new Date()).diff(sensorData.lastValueAt.toString())).asHours() > 24 ? { color: 'red' } : {} )}>
                                    {getSensorTypeName(sensorData.typeId, sensorTypes, t)} {moment(sensorData.lastValueAt.toString()).locale(i18n.language).format('l LTS')}
                                  </Grid>
                                ))
                              ) : (
                                <Grid item>{t('no_information_available', 'No information available')}</Grid>
                              )
                            }
                          </Grid>
                        </Grid>
                      ))
                    }
                  </React.Fragment>
                }>
                  <InfoCircleOutlined style={(hasPatientValuesOlderThan24h() ? { color: 'red'} : {})}/>
                </HtmlTooltip>
              </Grid>
            }
          </Grid>
          { patient.aiTags && 
            <Grid item xs={12}>
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    listStyle: 'none',
                    p: 0.5,
                    m: 0
                  }}
                  component="ul"
                >
                  {
                    patient.aiTags!!.map((aITag: IAITag, index: number) => (
                      <ListItem disablePadding key={index} sx={{ width: 'auto', pr: 0.75, pb: 0.75 }}>
                        <Chip sx={{ backgroundColor: aITag.color, color: 'white', borderColor: aITag.color }} variant="outlined" size="small" label={t(aITag.literalKey, aITag.literalKey)} />
                      </ListItem>
                    ))
                  }
                </Box>
              </Box>
            </Grid>
          }
        </Grid>
      </MainCard>
    </div>
  );
};

export default PatientCard